import { JsonValue, getOwn, isJsonObject, stableStringify } from "../json";
import { Path, PathSegment } from "../types/type-system";
import { Expr } from "./ast";
import { getFunction } from "./functions";

export interface EvalEnv {
  /** The resolved automation context (trigger, steps, run, ...). */
  data: JsonValue;
  now: string;
  vars?: ReadonlyMap<string, JsonValue>;
}

/**
 * Where a field's value lives on its parent object. Fields with arguments are
 * stored once per argument set, which is also how resolvers write fetched
 * values back into the context.
 */
export function fieldKey(segment: PathSegment): string {
  if (!segment.args || Object.keys(segment.args).length === 0) {
    return segment.field;
  }
  return `${segment.field}(${stableStringify(segment.args)})`;
}

export function readPath(root: JsonValue | undefined, path: Path): JsonValue {
  let current: JsonValue | undefined = root;
  for (let i = 0; i < path.length; i += 1) {
    const segment = path[i];
    if (!segment) {
      break;
    }
    if (Array.isArray(current)) {
      const rest = path.slice(i);
      return current.map((item) => readPath(item, rest));
    }
    if (!isJsonObject(current)) {
      return null;
    }
    current = getOwn(current, fieldKey(segment));
  }
  return current === undefined ? null : current;
}

export function hasPath(root: JsonValue | undefined, path: Path): boolean {
  let current: JsonValue | undefined = root;
  for (let i = 0; i < path.length; i += 1) {
    const segment = path[i];
    if (!segment) {
      break;
    }
    if (Array.isArray(current)) {
      const rest = path.slice(i);
      return current.every((item) => hasPath(item, rest));
    }
    if (!isJsonObject(current)) {
      return current === null;
    }
    const key = fieldKey(segment);
    if (!Object.hasOwn(current, key)) {
      return false;
    }
    current = current[key];
  }
  return true;
}

export function evaluate(expr: Expr, env: EvalEnv): JsonValue {
  switch (expr.kind) {
    case "literal":
      return expr.value;
    case "ref":
      return readPath(env.data, expr.path);
    case "var": {
      const value = env.vars?.get(expr.name);
      return value === undefined ? null : readPath(value, expr.path);
    }
    case "lambda":
      throw new Error("A predicate cannot be evaluated on its own");
    case "call": {
      const fn = getFunction(expr.fn);
      if (!fn) {
        throw new Error(`Unknown function ${expr.fn}`);
      }
      const memo = new Map<number, JsonValue>();
      return fn.evaluate({
        count: expr.args.length,
        now: env.now,
        arg(index) {
          const cached = memo.get(index);
          if (cached !== undefined) {
            return cached;
          }
          const arg = expr.args[index];
          const value = arg ? evaluate(arg, env) : null;
          memo.set(index, value);
          return value;
        },
        lambda(index) {
          const arg = expr.args[index];
          if (!arg || arg.kind !== "lambda") {
            throw new Error(
              `Argument ${index + 1} of ${expr.fn} must be a predicate`,
            );
          }
          return (value) => {
            const vars = new Map(env.vars ?? []);
            vars.set(arg.param, value);
            return evaluate(arg.body, { ...env, vars });
          };
        },
      });
    }
  }
}
